import { Controller } from "@hotwired/stimulus"

// Handles Linear issue fetching, selection, and import for Linear Issues projects
export default class extends Controller {
  static targets = [
    "stateFilter",
    "priorityFilter",
    "searchInput",
    "selectAllCheckbox",
    "selectedCount",
    "fetchButton",
    "importButton",
    "issueList",
    "loadingIndicator"
  ]

  static values = {
    projectId: String,
    fetchUrl: String,
    importUrl: String
  }

  connect() {
    console.log("Linear import controller connected", this.projectIdValue)
    this.issues = []
    this.selectedIssueIds = new Set()
  }

  async fetchIssues() {
    this.showLoading()

    try {
      const response = await fetch(this.fetchUrlValue, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        credentials: 'same-origin'
      })

      const data = await response.json()

      if (data.success) {
        this.issues = data.issues
        this.renderIssues()
      } else {
        this.showError(data.error || 'Failed to fetch issues')
      }
    } catch (error) {
      console.error('Error fetching issues:', error)
      this.showError('Failed to fetch issues. Please try again.')
    } finally {
      this.hideLoading()
    }
  }

  renderIssues() {
    const filteredIssues = this.getFilteredIssues()

    if (filteredIssues.length === 0) {
      this.issueListTarget.innerHTML = `
        <div class="flex items-center justify-center p-8 text-base-content/50">
          <div class="text-center">
            <i class="fas fa-inbox text-4xl mb-2"></i>
            <p>No issues match your filters</p>
          </div>
        </div>
      `
      return
    }

    const html = filteredIssues.map(issue => this.renderIssueRow(issue)).join('')
    this.issueListTarget.innerHTML = `<div class="divide-y divide-base-300">${html}</div>`

    // Restore checkbox states
    this.selectedIssueIds.forEach(id => {
      const checkbox = this.issueListTarget.querySelector(`[data-issue-id="${id}"]`)
      if (checkbox) checkbox.checked = true
    })

    this.updateSelectedCount()
  }

  renderIssueRow(issue) {
    const isDisabled = issue.already_imported || issue.imported_to_project
    const disabledClass = isDisabled ? 'opacity-50' : ''
    const checkboxDisabled = isDisabled ? 'disabled' : ''

    let statusBadge = ''
    if (issue.already_imported) {
      statusBadge = '<span class="badge badge-sm badge-success">Imported</span>'
    } else if (issue.imported_to_project) {
      statusBadge = `<span class="badge badge-sm badge-warning" title="Imported to ${issue.imported_to_project.name}">In Other Project</span>`
    }

    const priorityClass = {
      'urgent': 'badge-error',
      'high': 'badge-warning',
      'medium': 'badge-info',
      'low': 'badge-ghost',
      'no_priority': 'badge-ghost'
    }[issue.priority] || 'badge-ghost'

    const labels = (issue.labels || []).slice(0, 2).map(label => {
      const labelName = typeof label === 'object' ? label.name : label
      return `<span class="badge badge-sm badge-outline">${this.escapeHtml(labelName)}</span>`
    }).join('')

    return `
      <div class="flex items-center gap-4 p-4 hover:bg-base-200 transition-colors ${disabledClass}">
        <input type="checkbox"
               class="checkbox checkbox-sm"
               data-issue-id="${issue.id}"
               data-action="change->linear-import#toggleIssue"
               ${checkboxDisabled}>

        <div class="flex-shrink-0">
          <span class="badge badge-lg badge-primary font-mono">${this.escapeHtml(issue.identifier)}</span>
        </div>

        <div class="flex-1 min-w-0">
          <div class="font-medium truncate">${this.escapeHtml(issue.title)}</div>
          <div class="flex flex-wrap items-center gap-2 mt-1">
            <span class="badge badge-sm ${priorityClass}">${this.escapeHtml(issue.priority || 'No Priority')}</span>
            <span class="badge badge-sm badge-ghost">${this.escapeHtml(issue.state || 'Unknown')}</span>
            ${labels}
            ${statusBadge}
          </div>
        </div>

        <div class="flex-shrink-0 text-sm text-base-content/50">
          ${issue.team_name ? this.escapeHtml(issue.team_name) : ''}
        </div>
      </div>
    `
  }

  getFilteredIssues() {
    let filtered = [...this.issues]

    // Filter by state
    const stateFilter = this.hasStateFilterTarget ? this.stateFilterTarget.value : ''
    if (stateFilter) {
      filtered = filtered.filter(issue =>
        issue.state?.toLowerCase().includes(stateFilter.toLowerCase())
      )
    }

    // Filter by priority
    const priorityFilter = this.hasPriorityFilterTarget ? this.priorityFilterTarget.value : ''
    if (priorityFilter) {
      filtered = filtered.filter(issue => issue.priority === priorityFilter)
    }

    // Filter by search
    const searchTerm = this.hasSearchInputTarget ? this.searchInputTarget.value.toLowerCase() : ''
    if (searchTerm) {
      filtered = filtered.filter(issue =>
        issue.title?.toLowerCase().includes(searchTerm) ||
        issue.description?.toLowerCase().includes(searchTerm) ||
        issue.identifier?.toLowerCase().includes(searchTerm)
      )
    }

    return filtered
  }

  filterIssues() {
    this.renderIssues()
  }

  toggleIssue(event) {
    const checkbox = event.target
    const issueId = checkbox.dataset.issueId

    if (checkbox.checked) {
      this.selectedIssueIds.add(issueId)
    } else {
      this.selectedIssueIds.delete(issueId)
    }

    this.updateSelectedCount()
    this.updateSelectAllCheckbox()
  }

  toggleSelectAll(event) {
    const checked = event.target.checked
    const checkboxes = this.issueListTarget.querySelectorAll('input[type="checkbox"]:not(:disabled)')

    checkboxes.forEach(checkbox => {
      checkbox.checked = checked
      const issueId = checkbox.dataset.issueId
      if (checked) {
        this.selectedIssueIds.add(issueId)
      } else {
        this.selectedIssueIds.delete(issueId)
      }
    })

    this.updateSelectedCount()
  }

  updateSelectAllCheckbox() {
    if (!this.hasSelectAllCheckboxTarget) return

    const checkboxes = this.issueListTarget.querySelectorAll('input[type="checkbox"]:not(:disabled)')
    const checkedCount = this.issueListTarget.querySelectorAll('input[type="checkbox"]:checked').length

    this.selectAllCheckboxTarget.checked = checkboxes.length > 0 && checkedCount === checkboxes.length
    this.selectAllCheckboxTarget.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length
  }

  updateSelectedCount() {
    const count = this.selectedIssueIds.size

    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = `${count} selected`
    }

    if (this.hasImportButtonTarget) {
      this.importButtonTarget.disabled = count === 0
    }
  }

  async importSelected() {
    if (this.selectedIssueIds.size === 0) return

    const issueIds = Array.from(this.selectedIssueIds)

    // Show loading state
    if (this.hasImportButtonTarget) {
      this.importButtonTarget.classList.add('loading')
      this.importButtonTarget.disabled = true
    }

    try {
      const response = await fetch(this.importUrlValue, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        credentials: 'same-origin',
        body: JSON.stringify({ issue_ids: issueIds })
      })

      const data = await response.json()

      if (data.success) {
        // Show success message
        this.showToast(data.message, 'success')

        // Clear selections
        this.selectedIssueIds.clear()
        this.updateSelectedCount()

        // Refresh the page to show imported stories
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      } else {
        this.showToast(data.error || 'Failed to import issues', 'error')
      }
    } catch (error) {
      console.error('Error importing issues:', error)
      this.showToast('Failed to import issues. Please try again.', 'error')
    } finally {
      if (this.hasImportButtonTarget) {
        this.importButtonTarget.classList.remove('loading')
        this.importButtonTarget.disabled = this.selectedIssueIds.size === 0
      }
    }
  }

  showLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
      this.loadingIndicatorTarget.classList.add('flex')
    }
    if (this.hasIssueListTarget) {
      this.issueListTarget.classList.add('hidden')
    }
    if (this.hasFetchButtonTarget) {
      this.fetchButtonTarget.classList.add('loading')
    }
  }

  hideLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
      this.loadingIndicatorTarget.classList.remove('flex')
    }
    if (this.hasIssueListTarget) {
      this.issueListTarget.classList.remove('hidden')
    }
    if (this.hasFetchButtonTarget) {
      this.fetchButtonTarget.classList.remove('loading')
    }
  }

  showError(message) {
    this.issueListTarget.innerHTML = `
      <div class="alert alert-error m-4">
        <i class="fas fa-exclamation-circle"></i>
        <span>${this.escapeHtml(message)}</span>
      </div>
    `
  }

  showToast(message, type = 'info') {
    // Create toast element
    const toast = document.createElement('div')
    toast.className = `toast toast-end`
    toast.innerHTML = `
      <div class="alert alert-${type === 'success' ? 'success' : type === 'error' ? 'error' : 'info'}">
        <span>${this.escapeHtml(message)}</span>
      </div>
    `

    document.body.appendChild(toast)

    // Auto-remove after 3 seconds
    setTimeout(() => {
      toast.remove()
    }, 3000)
  }

  getCsrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ''
  }

  escapeHtml(text) {
    if (!text) return ''
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
